<template>
  <Transition name="customer-group-modal-overlay">
    <div
      v-if="visible"
      class="customer-group-modal-overlay customer-group-modal-overlay--glass"
      @click.self="$emit('cancel')"
    >
      <Transition name="customer-group-modal-panel" appear>
        <div
          v-if="visible"
          class="customer-group-modal-card customer-group-modal-card--glass"
          role="dialog"
          aria-modal="true"
          aria-labelledby="note-tag-modal-title"
          @click.stop
        >
          <div class="customer-group-modal-header customer-group-modal-header--glass">
            <h2 id="note-tag-modal-title" class="customer-group-modal-title">
              {{ modalTitle }}
            </h2>
            <button
              type="button"
              class="customer-group-modal-close-btn"
              aria-label="Close"
              title="Close"
              @click="$emit('cancel')"
            >
              <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          </div>

          <div class="customer-group-modal-body customer-group-modal-body--glass note-tag-modal-body">
            <LabelInputField
              input-id="note-tag-name"
              label="Name"
              v-model="tagName"
              placeholder="e.g. Follow-up"
              required
              flex
              :has-error="!!error"
              :disabled="saving"
              @input="error = ''"
            />
            <LabelDropdown
              label="Related To"
              select-id="note-tag-related-to"
              v-model="relatedTo"
              flex
              wide
              :options="relatedToOptions"
              :disabled="saving"
            />
            <div class="note-tag-color-field">
              <span class="note-tag-color-field__label">Color</span>
              <div class="note-tag-color-field__swatches" role="group" aria-label="Tag color">
                <button
                  v-for="color in colorPresets"
                  :key="color"
                  type="button"
                  class="note-tag-color-swatch"
                  :class="{ 'note-tag-color-swatch--selected': tagColor === color }"
                  :style="{ backgroundColor: color }"
                  :title="color"
                  :disabled="saving"
                  @click="tagColor = color"
                ></button>
              </div>
              <div class="note-tag-color-field__picker-row">
                <span
                  class="note-tag-color-field__preview-strip"
                  :style="{ backgroundColor: tagColor }"
                  aria-hidden="true"
                ></span>
                <input
                  id="note-tag-color"
                  v-model="tagColor"
                  type="color"
                  class="note-tag-color-input"
                  :disabled="saving"
                  aria-label="Custom tag color"
                />
              </div>
            </div>
            <div v-if="error" class="customer-group-modal-error">{{ error }}</div>
          </div>

          <div class="customer-group-modal-actions customer-group-modal-actions--glass">
            <SecondaryButton label="Cancel" :disabled="saving" @click="$emit('cancel')" />
            <PrimaryButton label="Save" :loading="saving" @click="ok" />
          </div>
        </div>
      </Transition>
    </div>
  </Transition>

  <ErrorModal
    :show="showErrorModal"
    title="Error"
    :message="errorMessage"
    buttonText="OK"
    @close="closeErrorModal"
  />
</template>

<script>
import '../styles/AddNewCustomerGroup.css';
import '../styles/NoteTagList.css';
import noteTagService from '@/services/noteTag.service.js';
import ErrorModal from '@/components/ErrorModal.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'AddNewNoteTag',
  components: {
    ErrorModal,
    LabelInputField,
    LabelDropdown,
    PrimaryButton,
    SecondaryButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: [String, Number],
      required: true,
    },
    defaultRelatedTo: {
      type: String,
      default: 'customer',
    },
    editingItem: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      tagName: '',
      relatedTo: 'customer',
      tagColor: '#6366f1',
      error: '',
      saving: false,
      showErrorModal: false,
      errorMessage: '',
      colorPresets: [
        '#6366f1',
        '#8b5cf6',
        '#ec4899',
        '#ef4444',
        '#f59e0b',
        '#10b981',
        '#06b6d4',
        '#64748b',
      ],
      relatedToOptions: [
        { value: 'customer', label: 'Customer' },
        { value: 'vendor', label: 'Vendor' },
        { value: 'consultant', label: 'Consultant' },
      ],
    };
  },
  computed: {
    isEditMode() {
      return !!this.editingItem?.id;
    },
    modalTitle() {
      return this.isEditMode ? 'Edit Note Tag' : 'Note Tag';
    },
  },
  watch: {
    visible(open) {
      if (open) this.resetForm();
    },
    editingItem() {
      if (this.visible) this.resetForm();
    },
    defaultRelatedTo(value) {
      if (this.visible && !this.isEditMode && value) {
        this.relatedTo = value;
      }
    },
  },
  methods: {
    resetForm() {
      if (this.isEditMode) {
        this.tagName = this.editingItem.name || '';
        this.relatedTo = this.editingItem.relatedTo || 'customer';
        this.tagColor = this.editingItem.color || '#6366f1';
      } else {
        this.tagName = '';
        this.relatedTo = this.defaultRelatedTo || 'customer';
        this.tagColor = '#6366f1';
      }
      this.error = '';
    },
    async ok() {
      if (!this.tagName.trim()) {
        this.error = 'Name is required.';
        return;
      }
      if (!this.relatedTo) {
        this.error = 'Related To is required.';
        return;
      }

      this.saving = true;
      this.error = '';
      try {
        const payload = {
          name: this.tagName.trim(),
          companyId: this.companyId,
          relatedTo: this.relatedTo,
          color: this.tagColor,
        };
        const response = this.isEditMode
          ? await noteTagService.updateNoteTag(this.editingItem.id, payload)
          : await noteTagService.createNoteTag(payload);
        const tag = response?.data?.data || response?.data;
        this.$emit('ok', tag);
        if (!this.isEditMode) this.tagName = '';
        this.error = '';
      } catch (err) {
        this.errorMessage =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          `Failed to ${this.isEditMode ? 'update' : 'create'} note tag.`;
        this.showErrorModal = true;
      } finally {
        this.saving = false;
      }
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
  },
};
</script>
